import { useEffect, useState } from 'react';

import { Button } from './shadcn/button';
import { ButtonGroup } from './shadcn/button-group';
import { Input } from './shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './shadcn/select';

export interface PaginationProps {
  canGetNextPage: boolean;
  canGetPrevPage: boolean;
  prevPage: () => void;
  nextPage: () => void;
  currentPage: number;
  noOfPages: number | undefined;
  goToPage: (value: number) => void;
  testid?: string;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  isNewSearch?: boolean;
  setIsNewSearch?: (value: boolean) => void;
  totalCount?: number;
  filteredCount?: number;
  entityName?: string;
}

export const Pagination = ({
  canGetPrevPage,
  canGetNextPage,
  prevPage,
  nextPage,
  currentPage,
  noOfPages,
  goToPage,
  pageSize = 50,
  onPageSizeChange,
  isNewSearch,
  setIsNewSearch,
  totalCount,
  filteredCount,
  entityName = 'items',
}: PaginationProps) => {
  const [displayPageCount, setDisplayPageCount] = useState(noOfPages);
  const [currentPageString, setCurrentPageString] = useState('1');

  const [displayTotalCount, setDisplayTotalCount] = useState(totalCount);
  const [displayFilteredCount, setDisplayFilteredCount] = useState(filteredCount);

  useEffect(() => {
    if (totalCount !== undefined) {
      setDisplayTotalCount(totalCount);
    }
  }, [totalCount]);

  useEffect(() => {
    if (filteredCount !== undefined) {
      setDisplayFilteredCount(filteredCount);
    }
  }, [filteredCount]);

  useEffect(() => {
    if (noOfPages !== undefined) {
      setDisplayPageCount(noOfPages);
    }
  }, [noOfPages]);

  useEffect(() => {
    if (isNewSearch) {
      goToPage && goToPage(0);
      setCurrentPageString('1');
      setIsNewSearch?.(false);
    }
  }, [isNewSearch]);

  useEffect(() => {
    if (currentPage !== parseInt(currentPageString) - 1) {
      setCurrentPageString((currentPage + 1).toString());
    }
  }, [currentPage]);

  const handleChangePage = (value: string) => {
    setCurrentPageString(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (noOfPages === undefined) return;

    if (event.key === 'Enter') {
      const pageNumber = parseInt(currentPageString) - 1;

      if (pageNumber < 0 || pageNumber >= noOfPages) {
        goToPage && goToPage(currentPage);
        setCurrentPageString((currentPage + 1).toString());
        return;
      }

      goToPage && goToPage(pageNumber);
    }
  };

  return (
    <div className="flex items-center text-xs whitespace-nowrap">
      {/* Count information */}
      {displayTotalCount !== undefined && displayFilteredCount !== undefined && (
        <div className="mx-2 text-gray-500">
          Showing {displayFilteredCount.toLocaleString()} of{' '}
          <span className="font-medium text-gray-600">
            {displayTotalCount.toLocaleString()} {entityName}
          </span>
        </div>
      )}

      <div className="flex items-center gap-1.5 text-gray-600">
        <p className="mx-2">
          <span>Page</span>
          <Input
            type="number"
            min={1}
            max={displayPageCount}
            value={currentPageString}
            onChange={(e) => setCurrentPageString(e.target.value)}
            disabled={displayPageCount === 1}
            onKeyDown={handleKeyDown}
            className="mx-1.5 inline-flex h-auto w-auto px-2 py-1 text-xs"
          />
          of {displayPageCount ?? 0}
        </p>

        <ButtonGroup>
          <Button size="sm" onClick={prevPage} disabled={!canGetPrevPage}>
            Previous
          </Button>
          <Button size="sm" onClick={nextPage} disabled={!canGetNextPage}>
            Next
          </Button>
        </ButtonGroup>
      </div>

      {onPageSizeChange && (
        <div className="ml-2 flex items-center gap-2">
          <Select value={pageSize.toString()} onValueChange={(value) => onPageSizeChange(parseInt(value))}>
            <SelectTrigger className="h-auto px-2 py-1 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-gray-600">Rows</span>
        </div>
      )}
    </div>
  );
};
